"use client";

import { IconCheck, IconCopy, IconInfo } from "@decibeltrade/react-icons";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { Card } from "@/components/card/card";

export interface ReferralCode {
  code: string;
  used: boolean;
}

interface ReferralLinksCardProps {
  referralCodes: ReferralCode[];
  volumeThresholdMet: boolean;
}

export function ReferralLinksCard({ referralCodes, volumeThresholdMet }: ReferralLinksCardProps) {
  const t = useTranslations("points.referralLinksModal");

  return (
    <Card
      background="gradient"
      className={stack({
        p: { base: "16", md: "24" },
        w: "full",
        gap: "0",
        rounded: "350",
        alignItems: "stretch",
      })}
    >
      <h2
        className={css({
          textStyle: { base: "heading.md", md: "heading.lg" },
          color: "text.primary",
          textAlign: "left",
        })}
      >
        {t("title")}
      </h2>

      <div
        className={stack({
          gap: "8",
          pt: "20",
          w: "full",
          alignItems: "stretch",
        })}
      >
        {referralCodes.length === 0 ? (
          !volumeThresholdMet ? (
            <VolumeGate />
          ) : (
            <span
              className={css({
                textStyle: { base: "highlight.lg", md: "highlight.xl" },
                color: "text.disabled",
                textAlign: "left",
              })}
            >
              {t("noCodesYet")}
            </span>
          )
        ) : (
          referralCodes.map((item) => (
            <ReferralCodeRow key={item.code} code={item.code} used={item.used} />
          ))
        )}
      </div>
    </Card>
  );
}

interface ReferralCodeRowProps {
  code: string;
  used: boolean;
}

function VolumeGate() {
  const t = useTranslations("points.referralLinksModal");

  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "4",
        p: "16",
        rounded: "[4px]",
        bg: "background.secondary",
      })}
    >
      <div className={flex({ gap: "8", alignItems: "center" })}>
        <IconInfo className={css({ w: "16", h: "16", color: "text.secondary", flexShrink: "0" })} />
        <span
          className={css({
            textStyle: "highlight.lg",
            color: "text.primary",
          })}
        >
          {t("codesNotAvailable")}
        </span>
      </div>
      <span
        className={css({
          textStyle: "body.md",
          color: "text.tertiary",
          pl: "24",
        })}
      >
        {t("tradeToUnlock")}
      </span>
    </div>
  );
}

function ReferralCodeRow({ code, used }: ReferralCodeRowProps) {
  const t = useTranslations("points.referralLinksModal");
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (copied) return;
      await navigator.clipboard.writeText(`https://app.decibel.trade/r/${code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Intentionally no-op; avoid noisy UI on permission errors
    }
  };

  if (used) {
    return (
      <div
        className={flex({
          justify: "space-between",
          alignItems: "center",
          w: "full",
          gap: "8",
          flexWrap: { base: "wrap", md: "nowrap" },
        })}
      >
        <span
          className={css({
            textStyle: { base: "highlight.lg", md: "highlight.xl" },
            color: "text.disabled",
            wordBreak: "break-all",
            flex: "1",
            minW: "0",
            textAlign: "left",
          })}
        >
          https://app.decibel.trade/r/{code}
        </span>
        <span
          className={css({
            textStyle: { base: "highlight.lg", md: "highlight.xl" },
            color: "text.disabled",
            flexShrink: "0",
          })}
        >
          {t("used")}
        </span>
      </div>
    );
  }

  return (
    <div
      className={flex({
        justify: "space-between",
        alignItems: "center",
        w: "full",
        gap: "8",
        flexWrap: { base: "wrap", md: "nowrap" },
      })}
    >
      <span
        className={css({
          textStyle: { base: "highlight.lg", md: "highlight.xl" },
          color: "text.secondary",
          wordBreak: "break-all",
          flex: "1",
          minW: "0",
          textAlign: "left",
        })}
      >
        https://app.decibel.trade/r/{code}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        className={css({
          display: "flex",
          gap: "8",
          alignItems: "center",
          cursor: "pointer",
          bg: "transparent",
          border: "none",
          p: "0",
          flexShrink: "0",
          _hover: { opacity: "0.8" },
          _active: { opacity: "0.6" },
          transitionPreset: "opacity",
        })}
      >
        {copied ? (
          <IconCheck
            className={css({
              w: "20",
              h: "20",
              color: "text.secondary",
            })}
          />
        ) : (
          <IconCopy
            className={css({
              w: "20",
              h: "20",
              color: "text.secondary",
            })}
          />
        )}
        <span
          className={css({
            textStyle: { base: "highlight.lg", md: "highlight.xl" },
            color: "text.secondary",
            textDecoration: "underline",
          })}
        >
          {copied ? t("copied") : t("copy")}
        </span>
      </button>
    </div>
  );
}
